/**
 * Module 129 (WP-129.2) — the price registry: every coin price, reward, cap and
 * cooldown Blockway ships, in one place (`docs/Framework_Catalogs/Economy_Rules.md`,
 * decision D3).
 *
 * The rest of the code reads these values, never its own copy:
 * `constants/economy.ts`, `generator/reward-curve.ts` and the generation
 * config's power-up and SOLVE IT defaults re-export from here, and the
 * player-app's starting grant and portal knobs read it through the package.
 * The server allowlist (`supabase/migrations/`, latest `submit_economy_transaction`)
 * is held to it by `economy-registry-migration.test.ts`, and the rule book's
 * price table is generated from {@link economyTableRows} (`pnpm economy:table`;
 * `economy-registry.test.ts` fails on drift).
 *
 * Only what is live goes here. A Wave 3 feature adds its rows in the same
 * change that ships it; until then its planned prices live in its module's
 * CLAUDE.md, so no unread value can disagree with a real one.
 */
export const ECONOMY = {
    rewards: {
        /**
         * A first clear (Modules 96–97): `base + perLevel × level`, rounded down to
         * a multiple of `step` and clamped to the server's range `min`–`max`.
         * Endless puzzles add one `step` every `endlessPuzzlesPerStep` puzzles.
         * `base` and `perLevel` can be tuned in remote config.
         */
        levelComplete: {
            base: 50,
            perLevel: 1.5,
            min: 10,
            max: 600,
            step: 5,
            endlessPuzzlesPerStep: 4,
        },
        /** Daily Challenge clear, once per UTC day (Module 67, ×10 by 97). */
        dailyComplete: 200,
        /** Opening every How to Play topic, once ever (Module 101). */
        howToExplored: 100,
        /** Daily Bonus 7-day streak, day 1 first (Module 118). */
        dailyLogin: [50, 75, 100, 125, 150, 200, 300],
        /** The streak day that also grants one free power-up use. */
        dailyLoginTicketDay: 7,
        /** Quests, each paid once ever (Module 118). Order is the list's order. */
        quests: [
            { id: 'win_5', target: 5, coins: 50 },
            { id: 'powered_gate_3', target: 3, coins: 100 },
            { id: 'win_moves_spare', target: 1, coins: 75 },
            { id: 'use_powerup', target: 1, coins: 50 },
            { id: 'daily_finish', target: 1, coins: 100 },
            { id: 'reach_puzzle_20', target: 20, coins: 150 },
            { id: 'win_streak_3', target: 3, coins: 100 },
            { id: 'play_intermediate', target: 1, coins: 100 },
            { id: 'daily_streak_3', target: 3, coins: 200 },
            { id: 'win_50', target: 50, coins: 300 },
        ],
        /** `win_moves_spare` counts a win with at least this many moves left. */
        questMovesSpare: 3,
        /**
         * Module 132 (WP-132.1) — Milestones: Bronze → Silver → Gold → Platinum,
         * each tier paid once ever (`blockjam_milestone_<id>_<tier>`). Thresholds
         * "low and long" (user, 2026-10-25). Tier coins default to 50/150/400/1,000;
         * a tier that absorbed a Module 118 quest keeps that quest's higher amount
         * (CLAUDE.md D4). After Platinum, every `every` more of the counter is one
         * ★ step paying `endlessStep`, once ever per step (D13). Ids are server
         * kinds: never rename one.
         */
        milestones: {
            list: [
                { id: 'wins', tiers: [3, 25, 100, 500], coins: [50, 300, 400, 1000], every: 100 },
                /**
                 * Module 131 (WP-131.10, D25/D28) — `reach_puzzle` is now the BEGINNER
                 * track's Reach badge (it counted any track before). The id is kept so
                 * its paid tiers and server rows carry over untouched; the counter never
                 * goes down, so a tier earned on another track stays earned.
                 */
                { id: 'reach_puzzle', tiers: [10, 30, 75, 200], coins: [150, 150, 400, 1000], every: 50 },
                /** WP-131.10 (D29) — Intermediate's Reach badge: its ladder passes its `qualifyAt` (35). */
                { id: 'medium_reach', tiers: [10, 35, 75, 200], coins: [200, 200, 500, 1250], every: 50 },
                /** WP-131.10 (D29) — Expert's Reach badge. */
                { id: 'hard_reach', tiers: [10, 30, 75, 200], coins: [250, 250, 600, 1500], every: 50 },
                { id: 'win_run', tiers: [3, 5, 10, 20], coins: [100, 150, 400, 1000], every: 5 },
                { id: 'moves_spare', tiers: [1, 10, 50, 200], coins: [75, 150, 400, 1000], every: 50 },
                {
                    id: 'no_powerup_wins',
                    tiers: [5, 25, 100, 300],
                    coins: [50, 150, 400, 1000],
                    every: 100,
                },
                { id: 'powered_gates', tiers: [3, 25, 100, 400], coins: [100, 150, 400, 1000], every: 100 },
                { id: 'powerups_used', tiers: [1, 10, 50, 150], coins: [50, 150, 400, 1000], every: 50 },
                { id: 'dailies_cleared', tiers: [1, 5, 20, 60], coins: [100, 150, 400, 1000], every: 20 },
                { id: 'daily_streak', tiers: [3, 7, 14, 30], coins: [200, 150, 400, 1000], every: 30 },
                { id: 'login_streak', tiers: [3, 7, 14, 30], coins: [50, 150, 400, 1000], every: 30 },
                { id: 'medium_wins', tiers: [1, 10, 50, 200], coins: [100, 150, 400, 1000], every: 50 },
                { id: 'hard_wins', tiers: [1, 5, 25, 100], coins: [50, 150, 400, 1000], every: 25 },
                {
                    id: 'blocks_cleared',
                    tiers: [50, 500, 2500, 10000],
                    coins: [50, 150, 400, 1000],
                    every: 5000,
                },
                {
                    id: 'coins_spent',
                    tiers: [100, 1000, 5000, 20000],
                    coins: [50, 150, 400, 1000],
                    every: 10000,
                },
                /**
                 * One-off badge, no coins of its own: How to Play already pays
                 * `howToExplored` when it is explored. Empty `coins` = nothing to claim.
                 */
                { id: 'howto_explored', tiers: [1], coins: [], every: 0 },
                /**
                 * Module 152 (WP-152.3, V8) — "Unlock your first avatar": a one-off
                 * badge that pays once, teaching the avatar shop (server row
                 * `blockjam_milestone_avatar_unlocked_bronze`, migration 0026).
                 */
                { id: 'avatar_unlocked', tiers: [1], coins: [100], every: 0 },
                /** Module 133 (WP-133.1, D6) — Daily Missions completed, ever. */
                {
                    id: 'missions_completed',
                    tiers: [5, 25, 100, 300],
                    coins: [50, 150, 400, 1000],
                    every: 100,
                },
            ],
            /** Each ★ step after Platinum: flat, never rising (D13). */
            endlessStep: 250,
            /** The highest ★ step the server pays (`_p1` … `_p999`). */
            maxStep: 999,
        },
        /**
         * Module 133 (WP-133.1) — Daily Missions: three a UTC day, drawn from this
         * pool for the player's current track (CLAUDE.md D1). `coins` is the
         * mission's effort (D2: 50 / 100 / 150), paid under
         * `blockjam_mission_<slot>_<coins>`, once per slot per UTC day. `track` is
         * the track a win must be on (`any` = every track and the Daily); a Hard
         * player is never drawn a Beginner or Intermediate mission. `spare` is the
         * own moves a `spareWins` win needs (0 = unused). Finishing all three pays
         * `setBonusTickets` free power-up uses, not coins. Ids are save values:
         * never rename one.
         */
        missions: {
            pool: [
                { id: 'win_2', counter: 'wins', target: 2, coins: 50, track: 'any', spare: 0 },
                { id: 'gates_3', counter: 'poweredGates', target: 3, coins: 50, track: 'any', spare: 0 },
                {
                    id: 'blocks_40',
                    counter: 'blocksCleared',
                    target: 40,
                    coins: 50,
                    track: 'any',
                    spare: 0,
                },
                { id: 'powerup_1', counter: 'powerupsUsed', target: 1, coins: 50, track: 'any', spare: 0 },
                {
                    id: 'easy_wins_3',
                    counter: 'trackWins',
                    target: 3,
                    coins: 100,
                    track: 'beginner',
                    spare: 0,
                },
                {
                    id: 'medium_wins_3',
                    counter: 'trackWins',
                    target: 3,
                    coins: 100,
                    track: 'intermediate',
                    spare: 0,
                },
                {
                    id: 'hard_wins_2',
                    counter: 'trackWins',
                    target: 2,
                    coins: 100,
                    track: 'expert',
                    spare: 0,
                },
                { id: 'spare_3', counter: 'spareWins', target: 1, coins: 100, track: 'any', spare: 3 },
                { id: 'run_2', counter: 'runWins', target: 2, coins: 100, track: 'any', spare: 0 },
                { id: 'daily_1', counter: 'dailyCleared', target: 1, coins: 100, track: 'any', spare: 0 },
                {
                    id: 'hard_clean_2',
                    counter: 'cleanTrackWins',
                    target: 2,
                    coins: 150,
                    track: 'expert',
                    spare: 0,
                },
                { id: 'win_5', counter: 'wins', target: 5, coins: 150, track: 'any', spare: 0 },
                { id: 'run_3', counter: 'runWins', target: 3, coins: 150, track: 'any', spare: 0 },
                { id: 'spare_5', counter: 'spareWins', target: 1, coins: 150, track: 'any', spare: 5 },
            ],
            setBonusTickets: 1,
        },
        /** A new wallet's first coins (Module 97). Remote config can override it. */
        startingCoins: 100,
        /** The ads-only Shop's "watch an ad" row (embed only). */
        shopAdTopUp: 100,
        /** "Thanks for watching" after an ad break; below the smallest puzzle reward. */
        adBreakThanks: 10,
        /**
         * Module 137 — coins one `coins_1000` purchase grants (US$1.00, set in
         * Playgama's console, not here). Bridge only: the Bridge wallet is local,
         * so it has no server row (`economy-registry-migration.test.ts`).
         */
        iapCoinPack: 1000,
        /**
         * Module 126 (D14) — the Welcome gift for signing in, once ever per save,
         * with the sign-in-only Nimbus avatar. Bridge only (sign-in exists only
         * there): no server row (`economy-registry-migration.test.ts`).
         */
        welcomeGift: 250,
    },
    /** In-puzzle power-ups (Modules 90–92, ×10 by 97). Ids are the plugin's. */
    powerups: {
        rainbowGates: { coins: 500, freeUses: 2, perPuzzleCap: 1 },
        hammer: { coins: 1000, freeUses: 2, perPuzzleCap: 1 },
        surge: { coins: 500, freeUses: 2, perPuzzleCap: 1 },
        colorDelete: { coins: 1500, freeUses: 2, perPuzzleCap: 1 },
        /** Retired (Module 92): no server row, never offered. */
        gateWidener: { coins: 500, freeUses: 2, perPuzzleCap: 1 },
    },
    sinks: {
        /** SOLVE IT (Module 83): beside a rewarded ad, and where there are no ads. */
        reveal: { coins: 2000, coinsWithoutAds: 2500 },
        /** Out-of-moves rescue for coins: the i-th buy in an attempt costs `prices[i]`. */
        extraMoves: { prices: [300, 600], grant: 5 },
        /** Keep the Daily Bonus streak after missing one day. */
        streakRepair: 200,
        /** One more try at a failed Daily Challenge, once per UTC day. */
        dailyRetry: 300,
    },
    /**
     * Module 152 (V7, approved by the user 2026-10-31) — coin avatars. Five tiers
     * on Module 119's theme ladder; every avatar's tier; and the released wave
     * (Wave 2's 13 avatars appear when this is raised to 2). Cosmetic only:
     * nothing here touches play (Economy rule 5).
     */
    avatars: {
        tierPrices: { common: 500, rare: 1000, epic: 2000, legendary: 3500, mythic: 5000 },
        tiers: {
            kitty: 'common',
            bunny: 'common',
            party: 'common',
            shades: 'common',
            sleepy: 'common',
            frog: 'common',
            fox: 'rare',
            panda: 'rare',
            keymaster: 'rare',
            ace: 'rare',
            ninja: 'rare',
            dj: 'rare',
            rosie: 'rare',
            owl: 'rare',
            bloom: 'rare',
            pearl: 'rare',
            captain: 'rare',
            alien: 'rare',
            starlight: 'epic',
            fairy: 'epic',
            robot: 'epic',
            dragon: 'epic',
            ghost: 'epic',
            boom: 'epic',
            hardhat: 'epic',
            dapper: 'epic',
            knight: 'epic',
            blaze: 'epic',
            wizard: 'epic',
            wild: 'legendary',
            unicorn: 'legendary',
            volt: 'legendary',
            royal: 'mythic',
        },
        waveLive: 1,
    },
    caps: {
        /** Ad and coin rescues together, per attempt (Module 118). */
        rescuesPerAttempt: 2,
        /** Moves each rewarded moves-ad grants, in order. */
        adMoveGrants: [3, 3],
        /** Rewarded moves-ads per puzzle. */
        maxAdsPerLevel: 2,
        /** Home Free Coins: wait after a rewarded view (Module 123). */
        freeCoinsCooldownMs: 60 * 60 * 1000,
    },
    /**
     * Module 131 (WP-131.7, D6) — staged unlocks (Economy_Rules rule 10): each Wave 3
     * system opens at this many TOTAL wins (every track and the Daily: the
     * `wins` milestone counter). Milestones is there from the start, so it has no
     * row. A system shows as locked only once its module has shipped it (D7).
     */
    unlocks: {
        missions: 3,
        winStreak: 5,
        boosters: 8,
        collection: 12,
        themes: 12,
    },
};
const UNLOCK_NAMES = {
    missions: 'Daily Missions',
    winStreak: 'Win Streak',
    boosters: 'Boosters',
    collection: 'Collection',
    themes: 'Board Themes',
};
const POWERUP_NAMES = {
    rainbowGates: 'Wild Gates',
    hammer: 'Hammer',
    surge: 'Master Key',
    colorDelete: 'Colour Bomb',
    gateWidener: 'Gate Widener (retired)',
};
/**
 * The registry as table rows, in rule-book order. Every leaf of {@link ECONOMY}
 * belongs to exactly one row, so the generated table can't silently omit a
 * value.
 */
export function economyTableRows() {
    const { rewards, powerups, sinks, caps, avatars } = ECONOMY;
    const lc = rewards.levelComplete;
    const row = (group, item, values, unit, ...paths) => ({ group, item, values, unit, paths });
    const rows = [
        row('Rewards', 'Puzzle clear: base', [lc.base], 'coins', 'rewards.levelComplete.base'),
        row('Rewards', 'Puzzle clear: per level', [lc.perLevel], 'coins per level', 'rewards.levelComplete.perLevel'),
        row('Rewards', 'Puzzle clear: min · max', [lc.min, lc.max], 'coins', 'rewards.levelComplete.min', 'rewards.levelComplete.max'),
        row('Rewards', 'Puzzle clear: step', [lc.step], 'coins', 'rewards.levelComplete.step'),
        row('Rewards', 'Endless: +1 step every N puzzles', [lc.endlessPuzzlesPerStep], 'count', 'rewards.levelComplete.endlessPuzzlesPerStep'),
        row('Rewards', 'Daily Challenge clear', [rewards.dailyComplete], 'coins', 'rewards.dailyComplete'),
        row('Rewards', 'How to Play explored', [rewards.howToExplored], 'coins', 'rewards.howToExplored'),
        row('Rewards', 'Daily Bonus days 1–7', rewards.dailyLogin, 'coins', ...rewards.dailyLogin.map((_, i) => `rewards.dailyLogin.${i}`)),
        row('Rewards', 'Daily Bonus ticket day', [rewards.dailyLoginTicketDay], 'count', 'rewards.dailyLoginTicketDay'),
        row('Rewards', 'Starting coins', [rewards.startingCoins], 'coins', 'rewards.startingCoins'),
        row('Rewards', 'Shop ad top-up', [rewards.shopAdTopUp], 'coins', 'rewards.shopAdTopUp'),
        row('Rewards', 'Ad break thanks', [rewards.adBreakThanks], 'coins', 'rewards.adBreakThanks'),
        row('Rewards', 'Coin pack (US$1.00, Bridge only)', [rewards.iapCoinPack], 'coins', 'rewards.iapCoinPack'),
        row('Rewards', 'Welcome gift for signing in (Bridge only)', [rewards.welcomeGift], 'coins', 'rewards.welcomeGift'),
        ...rewards.quests.map((q, i) => row('Quests', `${q.id} (target ${q.target})`, [q.coins], 'coins', `rewards.quests.${i}.id`, `rewards.quests.${i}.target`, `rewards.quests.${i}.coins`)),
        row('Quests', 'win_moves_spare: moves left', [rewards.questMovesSpare], 'moves', 'rewards.questMovesSpare'),
        ...rewards.milestones.list.flatMap((m, i) => {
            const at = `rewards.milestones.list.${i}`;
            const rows = [
                row('Milestones', `${m.id}: tiers${m.every > 0 ? ' · ★ every' : ''}`, m.every > 0 ? [...m.tiers, m.every] : m.tiers, 'count', `${at}.id`, ...m.tiers.map((_, t) => `${at}.tiers.${t}`), `${at}.every`),
            ];
            if (m.coins.length > 0) {
                rows.push(row('Milestones', `${m.id}: tier coins`, m.coins, 'coins', ...m.coins.map((_, t) => `${at}.coins.${t}`)));
            }
            return rows;
        }),
        row('Milestones', 'Each ★ step after Platinum', [rewards.milestones.endlessStep], 'coins', 'rewards.milestones.endlessStep'),
        row('Milestones', 'Highest ★ step paid', [rewards.milestones.maxStep], 'count', 'rewards.milestones.maxStep'),
        // Module 133 — one row per Daily Mission, then the set bonus.
        ...rewards.missions.pool.map((m, i) => {
            const at = `rewards.missions.pool.${i}`;
            const where = m.track === 'any' ? '' : ` on ${m.track}`;
            const spare = m.spare > 0 ? `, ${m.spare}+ moves left` : '';
            return row('Daily Missions', `${m.id}: ${m.counter} ${m.target}${where}${spare}`, [m.coins], 'coins', `${at}.id`, `${at}.counter`, `${at}.target`, `${at}.coins`, `${at}.track`, `${at}.spare`);
        }),
        row('Daily Missions', 'All three done: free power-up uses', [rewards.missions.setBonusTickets], 'count', 'rewards.missions.setBonusTickets'),
    ];
    for (const id of Object.keys(powerups)) {
        const p = powerups[id];
        const at = `powerups.${id}`;
        rows.push(row('Power-ups', `${POWERUP_NAMES[id]}: price`, [p.coins], 'coins', `${at}.coins`));
        rows.push(row('Power-ups', `${POWERUP_NAMES[id]}: free uses · per-puzzle cap`, [p.freeUses, p.perPuzzleCap], 'count', `${at}.freeUses`, `${at}.perPuzzleCap`));
    }
    rows.push(row('Sinks', 'SOLVE IT (with ads · without)', [sinks.reveal.coins, sinks.reveal.coinsWithoutAds], 'coins', 'sinks.reveal.coins', 'sinks.reveal.coinsWithoutAds'), row('Sinks', 'Extra moves: 1st · 2nd buy', sinks.extraMoves.prices, 'coins', ...sinks.extraMoves.prices.map((_, i) => `sinks.extraMoves.prices.${i}`)), row('Sinks', 'Extra moves: grant', [sinks.extraMoves.grant], 'moves', 'sinks.extraMoves.grant'), row('Sinks', 'Streak repair', [sinks.streakRepair], 'coins', 'sinks.streakRepair'), row('Sinks', 'Daily retry', [sinks.dailyRetry], 'coins', 'sinks.dailyRetry'), 
    // Module 152 — one row per avatar tier, naming the avatars at that price.
    ...Object.keys(avatars.tierPrices).map((tier) => row('Avatars', `${tier[0].toUpperCase()}${tier.slice(1)}: ${Object.entries(avatars.tiers)
        .filter(([, t]) => t === tier)
        .map(([id]) => id)
        .join(', ')}`, [avatars.tierPrices[tier]], 'coins', `avatars.tierPrices.${tier}`, ...Object.entries(avatars.tiers)
        .filter(([, t]) => t === tier)
        .map(([id]) => `avatars.tiers.${id}`))), row('Avatars', 'Released wave', [avatars.waveLive], 'count', 'avatars.waveLive'), row('Caps', 'Rescues per attempt', [caps.rescuesPerAttempt], 'count', 'caps.rescuesPerAttempt'), row('Caps', 'Moves per moves-ad', caps.adMoveGrants, 'moves', ...caps.adMoveGrants.map((_, i) => `caps.adMoveGrants.${i}`)), row('Caps', 'Moves-ads per puzzle', [caps.maxAdsPerLevel], 'count', 'caps.maxAdsPerLevel'), row('Caps', 'Free Coins cooldown', [caps.freeCoinsCooldownMs], 'ms', 'caps.freeCoinsCooldownMs'), ...Object.keys(ECONOMY.unlocks).map((id) => row('Unlocks', `${UNLOCK_NAMES[id]} opens at (total wins)`, [ECONOMY.unlocks[id]], 'count', `unlocks.${id}`)));
    return rows;
}
const fmt = (n) => n.toLocaleString('en-US');
/** The rule book's price table as markdown (unaligned; `pnpm economy:table` runs prettier). */
export function renderEconomyTable() {
    const lines = ['| Group | Item | Value | Unit |', '| --- | --- | --- | --- |'];
    for (const r of economyTableRows()) {
        lines.push(`| ${r.group} | ${r.item} | ${r.values.map(fmt).join(' · ')} | ${r.unit} |`);
    }
    return lines.join('\n');
}
